// libraries
import { BaseSelectors } from '@makemydeal/dr-activities-common';
import type { IVehicle, Vehicle, VehicleObjectForSimilaritySearch } from '@makemydeal/dr-platform-types';
import { createSelector } from 'reselect';

// types
import type {
    AdditionalSelectors,
    ChangeVehicleCustomVehicle,
    ChangeVehicleState,
    VehicleCustomData,
    StateTree,
    CatalogVehicleWithIndex,
    InitialSearchParameters,
    CatalogVehicle,
    FilterSummary,
    Dealership
} from '../types';
import type { AiAltVehicleOption } from '../types/AiAltVehicleOption';
import { AI_PROMPT_MODE } from '../constants';
import { buildVehicleForSimilaritySearch, buildVehicleIdentifier } from '../utils/agentUtils';
import { markOutOfStockByVin } from '../utils/aiInventoryFilter';

const DEFAULT_SELECTED_VEHICLE: CatalogVehicleWithIndex = {} as CatalogVehicleWithIndex;
const EMPTY_INITIAL_SEARCH_PARAMETERS: InitialSearchParameters = {};

export class Selectors extends BaseSelectors {
    constructor(public sliceName: string, public additionalSelectors: AdditionalSelectors) {
        super(sliceName);
    }

    getCvi = (state: StateTree): ChangeVehicleState => {
        return super.getState(state);
    };

    getShowBuildVehicleError = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.showBuildVehicleError || false;
    };

    getIsSearchError = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.isSearchError || false;
    };

    getShowProcessingError = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.showProcessingError || false;
    };

    getShowSearchError = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.showSearchError || false;
    };

    getIsFiltersInitialized = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.isFiltersInitialized || false;
    };

    getShowSearchLoader = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.isSearchInProgress || false;
    };

    getSearchResults = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.cviVehicles;
    };

    getIsVehicleSearchRequested = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.isVehicleSearchRequested;
    };

    getSearchSummary = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.summary;
    };

    getIsPermissionError = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.isPermissionError || false;
    };

    getIsStockNumberAvailable = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.isStockNumberAvailable;
    };

    getSearchQuery = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.searchQuery;
    };

    getCatalogYears = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.catalogYears || [];
    };

    getCatalogMakes = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.catalogMakes || [];
    };

    getCatalogModels = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.catalogModels || [];
    };

    getCatalogTrims = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.catalogTrims || [];
    };

    getCatalogVehicles = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.catalogVehicles || [];
    };

    getIsFetching = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return (changeVehicle.activeFetchCount || 0) > 0;
    };

    getShowCatalogError = (state: StateTree) => {
        const changeVehicle = this.getCvi(state);

        return !!changeVehicle.showCatalogError;
    };

    getSelectedVehicle = (state: StateTree): CatalogVehicleWithIndex => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.selectedVehicle || DEFAULT_SELECTED_VEHICLE;
    };

    isChangeProcessing = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.isChangeProcessing || false;
    };

    shouldOpenIncentivesAfterChange = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.openIncentivesAfterChange || false;
    };

    isApplyVehicleRequested = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.applyVehicleRequested || false;
    };

    isAddNewScenarioRequested = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.addNewScenarioRequested || false;
    };

    getCustomVehicle = (state: StateTree): ChangeVehicleCustomVehicle => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.customVehicle || {};
    };

    getAllVehicleCustomData = (state: StateTree): Record<string, VehicleCustomData> => {
        const changeVehicle = this.getCvi(state);

        return changeVehicle.vehicleCustomData || {};
    };

    getCustomDataForVehicle =
        (vin: string) =>
        (state: StateTree): VehicleCustomData => {
            const vehicleCustomData = this.getAllVehicleCustomData(state);
            const DEFAULT_VEHICLE_CUSTOM_DATA = {};

            return vehicleCustomData[vin] || DEFAULT_VEHICLE_CUSTOM_DATA;
        };

    getSelectedVehicleWithCustomData = (state: StateTree): IVehicle => {
        const selectedVehicle = this.getSelectedVehicle(state);

        // Identity shape alone cannot tell a real inventory vehicle from a built one: a vehicle built after arriving
        // from a row carries that row's vin (SearchAndSelectVehicleContent.tsx:139, spread onto every catalog result at
        // BuildVehicle.tsx:52), and an alternative vehicle recommendation carries a chromeStyleId. So both branches
        // below are reachable by both populations, and a recommendation without a stockNumber still lands in the
        // build branch and inherits the draft's prices. Left as-is deliberately — see the note in progress-bundle-9.
        if (selectedVehicle.vin && selectedVehicle.stockNumber) {
            const { mileage, listedPrice, retailPrice } = this.getCustomDataForVehicle(selectedVehicle.vin)(state);

            return {
                ...selectedVehicle,
                ...(listedPrice !== undefined && { listedPrice }),
                ...(mileage !== undefined && { mileage }),
                ...(retailPrice !== undefined && { retailPrice })
            };
        } else if (selectedVehicle.chromeStyleId) {
            const { msrp, sellingPrice, cost, odometer } = this.getCustomVehicle(state);
            return {
                ...selectedVehicle,
                retailPrice: msrp,
                listedPrice: sellingPrice,
                invoiceAmount: cost,
                cost,
                mileage: odometer
            };
        } else {
            return selectedVehicle;
        }
    };

    getVehicleWithCustomData =
        (vin: string) =>
        (state: StateTree): IVehicle | null => {
            const allVehicles = this.getSearchResults(state);
            const vehicle = allVehicles?.find((vehicle) => vehicle.vin === vin);

            if (vehicle?.vin) {
                const { mileage, listedPrice, retailPrice } = this.getCustomDataForVehicle(vehicle.vin)(state);

                return {
                    ...vehicle,
                    ...(listedPrice !== undefined && { listedPrice }),
                    ...(mileage !== undefined && { mileage }),
                    ...(retailPrice !== undefined && { retailPrice })
                };
            } else {
                return vehicle || null;
            }
        };

    shouldShowCost = (state: StateTree): boolean => {
        if (this.additionalSelectors.shouldShowCost) {
            return this.additionalSelectors.shouldShowCost(state);
        }

        return true;
    };

    getDealerDiscountsTotal = (state: StateTree): number => {
        return this.additionalSelectors.getDealerDiscountsTotal?.(state) ?? 0;
    };

    getDealerships = (state: StateTree): Dealership[] | undefined => {
        if (this.additionalSelectors.getDealerships) {
            return this.additionalSelectors.getDealerships(state);
        }

        return undefined;
    };

    getAiVehicleRecommendations = (state: StateTree): AiAltVehicleOption[] => {
        return this.additionalSelectors.getAiVehicleRecommendations?.(state) ?? [];
    };

    getAiVehicleRecommendationsLoading = (state: StateTree): boolean => {
        return this.additionalSelectors.getAiVehicleRecommendationsLoading?.(state) ?? false;
    };

    isAiAltVehicleOptionsActive = (state: StateTree): boolean => {
        return this.additionalSelectors.isAiAltVehicleOptionsActive?.(state) ?? false;
    };

    isAiAltVehicleOptionsSectionVisible = (state: StateTree): boolean => {
        return this.additionalSelectors.isAiAltVehicleOptionsSectionVisible?.(state) ?? false;
    };

    canCreateScenario = (state: StateTree): boolean => {
        return this.additionalSelectors.canCreateScenario?.(state) ?? false;
    };

    isAgeOfInventorySearchEnabled = (state: StateTree): boolean => {
        return this.additionalSelectors.isAgeOfInventorySearchEnabled?.(state) ?? false;
    };

    dispatchFetchIfStale = (): void => {
        this.additionalSelectors.dispatchFetchIfStale?.();
    };

    getCurrentDealer = (state: StateTree): Dealership | undefined => {
        if (this.additionalSelectors.getDealerships) {
            const name = this.additionalSelectors.getDealerName(state);
            const commonOrgId = this.additionalSelectors.getCommonOrgId(state);

            return {
                name,
                commonOrgId
            };
        }

        return undefined;
    };

    getVehicleVin = (state: StateTree): string | undefined => {
        if (this.additionalSelectors.getVehicleVin) {
            return this.additionalSelectors.getVehicleVin(state);
        }

        return undefined;
    };

    getVehicleStockNumber = (state: StateTree): string | undefined => {
        if (this.additionalSelectors.getVehicleStockNumber) {
            return this.additionalSelectors.getVehicleStockNumber(state);
        }

        return undefined;
    };

    getSearchPageCount = (state: StateTree): number => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.searchPageCount || 1;
    };

    getSearchCurrentPage = (state: StateTree): number => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.searchCurrentPage || 1;
    };

    getSearchTotalCount = (state: StateTree): number => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.searchTotalCount || 0;
    };

    getInitialSearchParameters = createSelector(
        (state: StateTree) => {
            if (this.additionalSelectors.getInitialSearchParameters) {
                return this.additionalSelectors.getInitialSearchParameters(state);
            }
            return null;
        },
        (result): InitialSearchParameters => result || EMPTY_INITIAL_SEARCH_PARAMETERS
    );
    getInitialPageSize = (state: StateTree): number | undefined => {
        return this.additionalSelectors.getInitialPageSize?.(state);
    };

    getAgentResponse = (state: StateTree): string => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.agentResponse || '';
    };

    /**
     * AI results, with the stock number cleared on a row live inventory no longer holds so it renders as
     * not in stock. Marked on read so the list is correct whichever request lands first; memoised
     * because `useSelector` compares by reference.
     */
    getAgentVehicleResponse = createSelector(
        (state: StateTree) => this.getCvi(state).agentVehicleResponse,
        (state: StateTree) => this.getCvi(state).aiSearchInactiveVin,
        (vehicles, inactiveVin): (Vehicle | CatalogVehicle)[] => markOutOfStockByVin(vehicles ?? [], inactiveVin)
    );

    getAiSearchInactiveVin = (state: StateTree): string | undefined => {
        return this.getCvi(state).aiSearchInactiveVin;
    };

    getAgentSearchPageCount = (state: StateTree): number => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.agentSearchPageCount || 1;
    };

    getAgentSearchCurrentPage = (state: StateTree): number => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.agentSearchCurrentPage || 1;
    };

    getAgentSearchTotalCount = (state: StateTree): number => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.agentSearchTotalCount || 0;
    };

    getShowAgentProcessingError = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.showAgentProcessingError || false;
    };

    getIsAgentFallbackPending = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.isAgentFallbackPending || false;
    };

    getIsShowingInventoryFallback = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.isShowingInventoryFallback || false;
    };

    getIsAgentSearchInProgress = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.isAgentSearchInProgress || false;
    };

    getAgentPromptString = (state: StateTree): string => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.agentPromptString || '';
    };

    getAgentPromptMode = (state: StateTree): string => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.agentPromptMode || AI_PROMPT_MODE.DEFAULT;
    };

    getAgentFilterSummary = (state: StateTree): FilterSummary | undefined => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.agentFilterSummary;
    };

    getAgentVehicleWithCustomData =
        (vin: string) =>
        (state: StateTree): IVehicle | null => {
            const allVehicles = this.getAgentVehicleResponse(state);

            // Find vehicle by VIN
            const vehicle = allVehicles.find((vehicle) => vehicle.vin === vin);

            // Return null if vehicle not found or doesn't have a VIN
            if (!vehicle || !vehicle.vin) {
                return null;
            }

            // Merge custom data with vehicle
            const { mileage, listedPrice, retailPrice } = this.getCustomDataForVehicle(vehicle.vin)(state);

            return {
                ...vehicle,
                ...(listedPrice !== undefined && { listedPrice }),
                ...(mileage !== undefined && { mileage }),
                ...(retailPrice !== undefined && { retailPrice })
            };
        };

    getShowAgentFilterError = (state: StateTree): boolean => {
        const changeVehicle = this.getCvi(state);
        return changeVehicle.showAgentFilterError || false;
    };

    getExistingVehicleIdentifier = (state: StateTree): string | undefined => {
        return buildVehicleIdentifier(this.getVehicle(state));
    };

    getVehicle = (state: StateTree): any => {
        return state.vehicle || {};
    };

    getCurrentVehicleForSimilaritySearch = (state: StateTree): VehicleObjectForSimilaritySearch | undefined => {
        return buildVehicleForSimilaritySearch(this.getVehicle(state));
    };
}
